<template>
  <div class="contact-us">
    <div class="page-header">
      <h2 class="page-title">联系我们</h2>
      <p class="page-desc">配置联系方式，展示在联系我们页面</p>
    </div>

    <el-card shadow="never">
      <el-form :model="contactForm" label-width="120px" v-loading="loading">
        <el-form-item label="姓名" required>
          <el-input v-model="contactForm.name" placeholder="请输入联系人姓名" />
          <div class="form-tip">建议填写公司对接人姓名</div>
        </el-form-item>

        <el-form-item label="手机号" required>
          <el-input v-model="contactForm.phone" placeholder="请输入手机号" />
          <div class="form-tip">请填写有效的手机号码</div>
        </el-form-item>

        <el-form-item label="微信号">
          <el-input v-model="contactForm.wechat" placeholder="请输入微信号" />
          <div class="form-tip">微信号方便客户添加微信咨询</div>
        </el-form-item>

        <el-form-item label="QQ号">
          <el-input v-model="contactForm.qq" placeholder="请输入QQ号" />
          <div class="form-tip">QQ号方便客户添加QQ咨询</div>
        </el-form-item>

        <el-form-item label="二维码">
          <div class="avatar-upload-container">
            <el-input v-model="contactForm.qrcode_url" placeholder="请输入二维码 URL" class="form-input" />
            <el-button type="success" @click="handleUploadAvatar">
              <el-icon><Upload /></el-icon> 上传二维码
            </el-button>
          </div>
          <div v-if="contactForm.qrcode_url" class="qrcode-preview">
            <el-image :src="contactForm.qrcode_url" fit="contain" style="width: 100px; height: 100px;" />
          </div>
          <div class="form-tip">建议尺寸：200x200像素，支持jpg、png、gif格式，推荐使用微信二维码</div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="saveContactInfo" :loading="saving">
            <el-icon><Check /></el-icon>保存配置
          </el-button>
          <el-button @click="fetchContactInfo" :loading="loading">刷新</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Check, Upload } from '@element-plus/icons-vue';
import { auth } from '@/utils/auth.js';
import config from '@/config.js';
import { beforeUpload, uploadToOSS, getUploadSignature } from '@/utils/uploadUtils';

export default {
  name: 'ContactUs',
  components: {
    Check,
    Upload
  },
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const saving = ref(false);
    
    const contactForm = reactive({
      id: 0,
      agent_id: 0,
      host_url: '',
      name: '',
      phone: '',
      wechat: '',
      qq: '',
      qrcode_url: ''
    });

    // 获取当前登录用户
    const getCurrentUser = () => {
      const user = auth.getStoredUser();
      if (!user) {
        ElMessage.error('未登录，请重新登录');
        router.push('/login');
        return null;
      }
      return user;
    };

    // 上传二维码
    const handleUploadAvatar = async () => {
      try {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = async (event) => {
          const file = event.target.files[0];
          if (!file) return;

          const isValid = await beforeUpload(file, {
            maxSize: 5,
            allowedTypes: ['image/jpeg', 'image/png', 'image/gif'],
          });
          if (!isValid) return;

          const signatureData = await getUploadSignature('image');
          if (!signatureData) {
            ElMessage.error("获取上传签名失败");
            return;
          }

          const uploadResult = await uploadToOSS(file, signatureData);
          if (uploadResult.success) {
            ElMessage.success("二维码上传成功");
            contactForm.qrcode_url = uploadResult.url;
          } else {
            ElMessage.error("二维码上传失败");
          }
        };
        fileInput.click();
      } catch (error) {
        console.error("上传二维码失败:", error);
        ElMessage.error("上传二维码失败，请重试");
      }
    };

    // 获取联系我们信息
    const fetchContactInfo = async () => {
      const user = getCurrentUser();
      if (!user) return;
      
      loading.value = true;
      try {
        const response = await axios.post(`${config.BASE_URL}/agent/api/contact_api.php`, {
          action: 'get',
          agent_id: user.id,
          host_url: config.BASE_URL
        });
        
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          contactForm.id = data.id || 0;
          contactForm.agent_id = data.agent_id || user.id;
          contactForm.host_url = data.host_url || config.BASE_URL;
          contactForm.name = data.name || '';
          contactForm.phone = data.phone || '';
          contactForm.wechat = data.wechat || '';
          contactForm.qq = data.qq || '';
          contactForm.qrcode_url = data.qrcode_url || '';
        } else {
          ElMessage.error(response.data.message || '获取信息失败');
        }
      } catch (error) {
        console.error('获取联系我们信息失败:', error);
        ElMessage.error('网络错误，请稍后重试');
      } finally {
        loading.value = false;
      }
    };

    // 保存联系我们信息
    const saveContactInfo = async () => {
      const user = getCurrentUser();
      if (!user) return;
      
      // 简单验证
      if (!contactForm.name) {
        ElMessage.warning('请填写联系人姓名');
        return;
      }
      if (!contactForm.phone) {
        ElMessage.warning('请填写手机号');
        return;
      }
      
      saving.value = true;
      try {
        const response = await axios.post(`${config.BASE_URL}/agent/api/contact_api.php`, {
          action: 'save',
          agent_id: user.id,
          host_url: contactForm.host_url || config.BASE_URL,
          name: contactForm.name,
          phone: contactForm.phone,
          wechat: contactForm.wechat,
          qq: contactForm.qq,
          qrcode_url: contactForm.qrcode_url
        });
        
        if (response.data.success) {
          ElMessage.success('保存成功');
          fetchContactInfo(); // 刷新数据
        } else {
          ElMessage.error(response.data.message || '保存失败');
        }
      } catch (error) {
        console.error('保存联系我们信息失败:', error);
        ElMessage.error('保存失败，请检查网络连接');
      } finally {
        saving.value = false;
      }
    };

    onMounted(() => {
      fetchContactInfo();
    });

    return {
      loading,
      saving,
      contactForm,
      handleUploadAvatar,
      fetchContactInfo,
      saveContactInfo
    };
  }
};
</script>

<style scoped>
.contact-us {
    padding: 0px;
}

.page-header {
    margin-bottom: 20px;
}

.page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
}

.page-desc {
    font-size: 14px;
    color: #8c8c8c;
}

.form-tip {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
    line-height: 1.4;
}

.avatar-upload-container {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 10px;
    margin-bottom: 10px;
}

.avatar-upload-container .el-input {
    flex: 1;
}

.qrcode-preview {
    margin-top: 10px;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    padding: 10px;
    display: inline-block;
}
</style>